import { clsx } from "clsx";
import { useEffect, useState } from "react";

const DO_LOGO_PATHS = [
  "M0.5 0.526855H11.9402C21.5565 0.526855 26.2063 6.15796 26.2063 14.8856C26.2063 24.4257 21.0642 30.2778 11.7981 30.2778H0.5V0.526855ZM8.69726 24.3772H11.4051C15.6969 24.3772 17.7153 21.4828 17.7153 15.0068C17.7153 9.40268 16.0375 6.46788 11.4976 6.46788H8.69726V24.3772Z",
  "M42.6572 0C32.648 0 28.5024 7.47041 28.5024 15.217C28.5024 15.3612 28.5024 15.5068 28.5024 15.6509H36.9988C36.9988 15.4973 36.9988 15.3383 36.9988 15.182C36.9988 9.65736 38.4741 6.0057 42.4024 6.0057C46.6941 6.0057 47.9495 9.91338 47.9495 15.1685C47.9495 15.3329 47.9495 15.496 47.9495 15.655H56.4136C56.4136 15.5202 56.4136 15.3855 56.4136 15.2507C56.411 7.19417 52.2251 0 42.6572 0Z",
];

const BU_LOGO_PATHS = [
  "M58.707 0.526855H71.8506C78.9588 0.526855 81.6076 4.2405 81.6076 8.14683C81.6076 11.6408 79.73 13.402 77.7383 14.1903C80.0183 15.0863 82.5665 17.1951 82.5665 21.2551C82.5665 26.7016 78.5752 30.2831 71.6427 30.2831H58.707V0.526855ZM69.997 12.0397C72.5788 12.0397 73.6893 11.1827 73.6893 9.0402C73.6893 6.94084 72.3186 6.01243 70.1781 6.01243H66.62V12.0397H69.997ZM66.62 24.8218H70.2572C72.4313 24.8218 74.1936 23.8328 74.1936 21.119C74.1936 18.641 72.8336 17.3743 69.9179 17.3743H66.62V24.8218Z",
  "M101.273 14.8452V19.2527C101.273 21.9827 100.592 24.2694 97.2122 24.2694C93.7439 24.2694 93.0371 21.883 93.0371 19.2527V14.8452H84.8694V18.955C84.8694 25.8567 88.1673 30.2738 97.0741 30.2738C106.03 30.2738 109.505 25.8972 109.505 18.9657V14.8452H101.273Z",
];

const ORBIT_PATH =
  "M 0 0 C 10 0 24 -18 24 -30 C 24 -42 -24 -48 -60 -48 C -102 -48 -136 -22 -136 0 C -136 22 -102 48 -60 48 C -24 48 24 42 24 30 C 24 18 10 0 0 0";
const WORDMARK_CENTER_X = 61.843;
const WORDMARK_CENTER_X_PERCENT = `${WORDMARK_CENTER_X}%`;
const FULL_PAGE_VISUAL_ALIGNMENT_SHIFT_PERCENT = `${(50 - WORDMARK_CENTER_X).toFixed(3)}%`;

/**
 * Animated DoBu logo loading indicator.
 * "Bu" orbits around "Do" in a smooth elliptical path, matching the brand animation.
 *
 * - delay: ms before showing (prevents flash on fast loads). Default 300ms.
 * - size: controls the logo width. "lg" for full-page, "md" for sections, "sm" for inline.
 * - fullPage: wraps in a full-viewport centered container.
 */
export default function LoadingLogo({
  size = "lg",
  fullPage = false,
  delay = 300,
  className,
}: {
  size?: "sm" | "md" | "lg";
  fullPage?: boolean;
  delay?: number;
  className?: string;
}) {
  const [visible, setVisible] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) {
      return;
    }
    const timer = setTimeout(() => {
      setVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  if (!visible) {
    return null;
  }

  const sizeClass = {
    sm: "w-40",
    md: "w-56",
    lg: "w-72",
  }[size];

  const logo = (
    <div
      className={clsx("inline-flex", className)}
      style={fullPage ? { transform: `translateX(${FULL_PAGE_VISUAL_ALIGNMENT_SHIFT_PERCENT})` } : undefined}
    >
      <div className="inline-flex flex-col justify-center animate-fadeIn">
        <svg
          className={clsx(sizeClass, "text-slate-700")}
          viewBox="-86 -56 228 148"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <defs>
            <path id="orbit-path" d={ORBIT_PATH} />
          </defs>
          <g fill="currentColor">
            {DO_LOGO_PATHS.map((path) => (
              <path key={path} d={path} />
            ))}
          </g>
          <g fill="currentColor">
            <g>
              <animateMotion
                dur="5s"
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.3;1"
                keyPoints="0;0;1"
                keySplines="0 0 1 1;0.37 0 0.18 1"
              >
                <mpath href="#orbit-path" xlinkHref="#orbit-path" />
              </animateMotion>
              {BU_LOGO_PATHS.map((path) => (
                <path key={path} d={path} />
              ))}
            </g>
          </g>
        </svg>

        {/* Anchor the dots to the visual center of the DoBu wordmark, not the padded SVG box. */}
        <div className="relative mt-1 h-2 w-full">
          <div className="absolute top-0 flex gap-2 -translate-x-1/2" style={{ left: WORDMARK_CENTER_X_PERCENT }}>
            <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[pulse_1.5s_infinite]" />
            <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[pulse_1.5s_infinite_0.3s]" />
            <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[pulse_1.5s_infinite_0.6s]" />
          </div>
        </div>
      </div>
    </div>
  );

  if (fullPage) {
    return <div className="flex items-center justify-center w-full min-h-[50vh]">{logo}</div>;
  }

  return logo;
}
